import React, { createContext, useContext, useState, useEffect } from 'react';
import { dataFetch } from '../api';

const GoalsContext = createContext();

export const useGoalsContext = () => {
  const context = useContext(GoalsContext);
  if (!context) throw new Error('useGoalsContext must be used within GoalsProvider');
  return context;
};

export const GoalsProvider = ({ children }) => {
  const [overallGoals, setOverallGoals] = useState([]);
  const [sessionGoals, setSessionGoals] = useState([]);

  useEffect(() => {
    const loadGoals = async () => {
      const userId = localStorage.getItem('user_id');
      if (!userId) return;
      
      const resOverall = await dataFetch(`read/overall_goals?owner_id=${userId}`);
      if (resOverall?.status === 'success') {
        const loadedOverall = (resOverall.data ?? []).map(g => {
          let parsedMilestones = [];
          if (g.milestones) {
            try {
              parsedMilestones = typeof g.milestones === 'string' ? JSON.parse(g.milestones) : g.milestones;
            } catch(e) {
              console.error("Failed to parse milestones on load", e);
            }
          }
          return {
            ...g,
            targetDate: g.targetdate || g.targetDate || '',
            progress: Number(g.progress) || 0,
            milestones: Array.isArray(parsedMilestones) ? parsedMilestones : []
          };
        });
        setOverallGoals(loadedOverall);
      }
      
      const resSession = await dataFetch(`read/session_goals?owner_id=${userId}`);
      if (resSession?.status === 'success') {
        const loadedSession = (resSession.data ?? []).map(g => ({
          ...g,
          completed: Boolean(g.completed)
        }));
        setSessionGoals(loadedSession);
      }
    };
    loadGoals();
  }, []);

  const addOverallGoal = async (goalData) => {
    const userId = localStorage.getItem('user_id');
    const newGoal = {
      ...goalData,
      targetdate: goalData.targetDate || '',
      milestones: JSON.stringify(goalData.milestones || []),
      progress: 0,
      status: 'In Progress',
      user_id: userId
    };
    delete newGoal.targetDate;

    const res = await dataFetch(`create/overall_goals?owner_id=${userId}`, {
      method: 'POST',
      body: JSON.stringify(newGoal)
    });
    
    if (res?.status === 'success') {
      const created = {
        ...goalData,
        ...(res.data || {})
      };
      
      created.targetDate = created.targetdate || created.targetDate || goalData.targetDate || '';
      created.progress = Number(created.progress) || Number(goalData.progress) || 0;
      
      let parsedMilestones = goalData.milestones || [];
      if (res.data?.milestones && typeof res.data.milestones === 'string') {
        try { 
          parsedMilestones = JSON.parse(res.data.milestones); 
        } catch(e) {
          console.error("Failed to parse milestones on create", e);
        }
      } else if (Array.isArray(res.data?.milestones)) {
        parsedMilestones = res.data.milestones;
      }
      created.milestones = parsedMilestones;

      setOverallGoals(prev => [created, ...prev]);
      return created;
    }
  };

  const updateOverallGoal = async (id, goalData) => {
    const userId = localStorage.getItem('user_id');
    const dataToUpdate = { ...goalData };
    
    if (dataToUpdate.milestones) {
      dataToUpdate.milestones = JSON.stringify(dataToUpdate.milestones);
    }
    
    if ('targetDate' in dataToUpdate) {
      dataToUpdate.targetdate = dataToUpdate.targetDate;
      delete dataToUpdate.targetDate;
    }
    
    const res = await dataFetch(`update/overall_goals/${id}?owner_id=${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ ...dataToUpdate, user_id: userId })
    });
    
    if (res?.status === 'success') {
      setOverallGoals(prev => prev.map(g => {
        if (String(g.id) === String(id)) {
          const updated = {
            ...g,
            ...goalData,
            ...(res.data || {})
          };
          updated.targetDate = updated.targetdate || updated.targetDate || g.targetDate || '';
          updated.progress = Number(updated.progress) || Number(g.progress) || 0;
          
          let parsedMilestones = updated.milestones || [];
          if (typeof updated.milestones === 'string') {
            try { 
              parsedMilestones = JSON.parse(updated.milestones); 
            } catch(e) {
              console.error("Failed to parse milestones on update", e);
            }
          }
          updated.milestones = Array.isArray(parsedMilestones) ? parsedMilestones : [];
          
          return updated;
        }
        return g;
      }));
    }
  };

  const deleteOverallGoal = async (id) => {
    const userId = localStorage.getItem('user_id');
    const res = await dataFetch(`delete/overall_goals/${id}?owner_id=${userId}`, {
      method: 'DELETE'
    });
    if (res?.status === 'success') {
      setOverallGoals(prev => prev.filter(g => String(g.id) !== String(id)));
    }
  };

  const toggleMilestone = async (goalId, milestoneId) => {
    const goal = overallGoals.find(g => String(g.id) === String(goalId));
    if (!goal) return;
    
    const updatedMilestones = goal.milestones.map(m => 
      String(m.id) === String(milestoneId) ? { ...m, completed: !m.completed } : m
    );
    
    const completedCount = updatedMilestones.filter(m => m.completed).length;
    const progress = updatedMilestones.length > 0 
      ? Math.round((completedCount / updatedMilestones.length) * 100) 
      : 0;
      
    await updateOverallGoal(goalId, { milestones: updatedMilestones, progress });
  };

  const addSessionGoal = async (goalData) => {
    const userId = localStorage.getItem('user_id');
    const newGoal = {
      ...goalData,
      completed: goalData.completed ? 1 : 0,
      user_id: userId
    };
    const res = await dataFetch(`create/session_goals?owner_id=${userId}`, {
      method: 'POST',
      body: JSON.stringify(newGoal)
    });
    
    if (res?.status === 'success') {
      const created = {
        ...goalData,
        ...(res.data || {})
      };
      created.completed = Boolean(created.completed);
      setSessionGoals(prev => [created, ...prev]);
      return created;
    }
  };

  const updateSessionGoal = async (id, goalData) => {
    const userId = localStorage.getItem('user_id');
    const dataToUpdate = { ...goalData };
    if ('completed' in dataToUpdate) {
      dataToUpdate.completed = dataToUpdate.completed ? 1 : 0;
    }
    const res = await dataFetch(`update/session_goals/${id}?owner_id=${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ ...dataToUpdate, user_id: userId })
    });
    
    if (res?.status === 'success') {
      setSessionGoals(prev => prev.map(g => {
        if (String(g.id) === String(id)) {
          const updated = {
            ...g,
            ...goalData,
            ...(res.data || {})
          };
          updated.completed = Boolean(updated.completed);
          return updated;
        }
        return g;
      }));
    }
  };

  const deleteSessionGoal = async (id) => {
    const userId = localStorage.getItem('user_id');
    const res = await dataFetch(`delete/session_goals/${id}?owner_id=${userId}`, {
      method: 'DELETE'
    });
    if (res?.status === 'success') {
      setSessionGoals(prev => prev.filter(g => String(g.id) !== String(id)));
    }
  };

  const toggleSessionGoalComplete = async (id) => {
    const goal = sessionGoals.find(g => String(g.id) === String(id));
    if (!goal) return;
    await updateSessionGoal(id, { completed: !goal.completed });
  };

  return (
    <GoalsContext.Provider
      value={{
        overallGoals,
        sessionGoals,
        addOverallGoal,
        updateOverallGoal,
        deleteOverallGoal,
        toggleMilestone,
        addSessionGoal,
        updateSessionGoal,
        deleteSessionGoal,
        toggleSessionGoalComplete
      }}
    >
      {children}
    </GoalsContext.Provider>
  );
};